import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { CHILDREN_DATA_OUTPUTS_KEY } from '@/utils/queryKeys.ts';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { StepDataOutputs } from '@/types/automationFields.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { get_children_data_outputs_url } from '@/utils/urls.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useRetrieveEditorLocalSubflowContext from '@/hooks/api/automations/useRetrieveEditorLocalSubflowContext.ts';

const useRetrieveAllChildrenDataOutputs = (): UseQueryResult<StepDataOutputs, AxiosError> => {
  const apiClient = useApiClient();

  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const automationId = useAutomationId();
  const latestVersionId = latestVersion?.id;
  const { target } = useActiveCompositionTarget();
  const subflowId = target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const localContext = useRetrieveEditorLocalSubflowContext(subflowId);

  const rootQuery = useQuery<StepDataOutputs, AxiosError>({
    queryKey: [...CHILDREN_DATA_OUTPUTS_KEY(automationId, latestVersionId || ''), target.scope_kind],
    queryFn: async () => {
      const response = await apiClient.get(get_children_data_outputs_url(latestVersionId));
      return response.data;
    },
    enabled: !!automationId && !!latestVersionId && target.scope_kind === 'root',
    placeholderData: keepPreviousData,
  });

  if (target.scope_kind === 'local_subflow') {
    return {
      ...localContext,
      data: localContext.data ? { step_data_outputs: localContext.data.children_data_outputs } : undefined,
    } as UseQueryResult<StepDataOutputs, AxiosError>;
  }

  return rootQuery;
};

export default useRetrieveAllChildrenDataOutputs;
